import type { InvokeApiDestinationConfig } from '~/types/workflow.types';

// The limits and patterns below mirror NodeParser::parseInvokeApiDestination so the editor can only
// build a config the control plane will take. Draft saves reject anything outside them with a 422 on
// the offending key, and because a save replaces the whole document that 422 blocks every other edit
// in the workflow.

export const MAX_BINDINGS = 10;
export const MAX_BINDING_KEY_LENGTH = 40;
export const MAX_BINDING_VALUE_LENGTH = 1000;
export const MAX_PAYLOAD_BYTES = 4096;
export const BINDING_KEY_PATTERN = /^[a-z]([a-z0-9_]*[a-z0-9])?$/;
export const JMESPATH_URL = 'https://jmespath.org/';
export const API_DESTINATIONS_DOC_URL = 'https://explore.voxie.com/voxiesupport/hc/en-us/articles/api-destinations';

export const PAYLOAD_PLACEHOLDER = `{
  "exampleKey.$": "contact.first_name"
}`;

export interface ApiDestinationSummary {
    id: string;
    team_id: number;
    friendly_name: string;
    http_method: string;
    uri_template: string;
    uri_hostname: string;
    uri_variables: string[];
    auth_config_id: string | null;
    automation_count: number;
    live_automation_count: number;
    // Present only when the team has workflow_builder.
    workflow_count?: number;
    live_workflow_count?: number;
    created_at: string;
    updated_at: string;
}

/**
 * A URI template variable may be any of `/^[a-zA-Z0-9_]{1,40}$/` (JmesPathUriTemplateProcessor), but
 * a workflow binding key must be a lowercase identifier. So `{userId}`, `{_id}` and `{id_}` are legal
 * in a destination yet can never be bound from a workflow — writing one 422s every draft save.
 */
export const isBindableVariable = (name: string): boolean =>
    name.length <= MAX_BINDING_KEY_LENGTH && BINDING_KEY_PATTERN.test(name);

export const payloadByteLength = (value: string): number => new TextEncoder().encode(value).length;

export const payloadOverCapError = (payload: string): string | undefined => {
    const bytes = payloadByteLength(payload);

    return bytes > MAX_PAYLOAD_BYTES ? `Payload is ${bytes} bytes; the limit is ${MAX_PAYLOAD_BYTES}.` : undefined;
};

// Keys ending in `.$` hold a JMESPath expression, so their value has to be a string — the same check
// JmesPathPayloadTemplateProcessor::validateTemplate makes at runtime.
const findNonStringExpression = (value: unknown, path: string[] = []): string | undefined => {
    if (Array.isArray(value)) {
        for (const [index, item] of value.entries()) {
            const found = findNonStringExpression(item, [...path, String(index)]);

            if (undefined !== found) {
                return found;
            }
        }

        return undefined;
    }

    if (null === value || 'object' !== typeof value) {
        return undefined;
    }

    for (const [key, item] of Object.entries(value)) {
        if (key.endsWith('.$') && 'string' !== typeof item) {
            return [...path, key].join('.');
        }

        const found = findNonStringExpression(item, [...path, key]);

        if (undefined !== found) {
            return found;
        }
    }

    return undefined;
};

export const payloadJsonError = (value: string): string | undefined => {
    if ('' === value.trim()) {
        return undefined;
    }

    let parsed: unknown;

    try {
        parsed = JSON.parse(value);
    } catch (error) {
        return `Not valid JSON: ${error instanceof Error ? error.message : String(error)}`;
    }

    const nonString = findNonStringExpression(parsed);

    return undefined === nonString ? undefined : `"${nonString}" must hold a JMESPath string.`;
};

export const normalizeConfig = (
    config: InvokeApiDestinationConfig | null | undefined
): InvokeApiDestinationConfig | null => {
    if (null === config || undefined === config) {
        return null;
    }

    const next: InvokeApiDestinationConfig = {};

    if (undefined !== config.destination_id && '' !== config.destination_id) {
        next.destination_id = config.destination_id;
    }

    if (undefined !== config.bindings && 0 !== Object.keys(config.bindings).length) {
        next.bindings = config.bindings;
    }

    if (undefined !== config.payload && '' !== config.payload) {
        next.payload = config.payload;
    }

    return 0 === Object.keys(next).length ? null : next;
};

export const isValidConfig = (config: InvokeApiDestinationConfig | null): boolean => {
    if (null === config) {
        return true;
    }

    const bindings = Object.entries(config.bindings ?? {});

    return (
        bindings.length <= MAX_BINDINGS &&
        bindings.every(
            ([variable, value]) => isBindableVariable(variable) && value.length <= MAX_BINDING_VALUE_LENGTH
        ) &&
        undefined === payloadOverCapError(config.payload ?? '')
    );
};

export const reconcileBindings = (
    previous: Record<string, string> | undefined,
    variables: string[]
): Record<string, string> | undefined => {
    const next: Record<string, string> = {};

    for (const variable of variables) {
        const carried = undefined !== previous && Object.hasOwn(previous, variable) ? previous[variable] ?? '' : '';

        if (isBindableVariable(variable) && '' !== carried) {
            next[variable] = carried;
        }
    }

    return 0 === Object.keys(next).length ? undefined : next;
};
